import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AclPermissionDetail } from '@y42/acl/models';
import { createAclPermissionDetail } from '@y42/acl/util/create-acl-permission-detail';
import { connectState, InputObservable, trackByValue } from '@y42/angular/util';
import {
  ContextMenuOption,
  ExportMode,
  intersectMenuOptions,
  ToolbarBorderStyle,
  ToolbarEmoji,
} from '@y42/canvas-components';
import {
  CHAT_ROOM_MESSAGE_ID_QUERY_PARAM,
  ChatRedirectionHelperService,
  ChatRoomCreationStrategy,
  ChatRoomUIModel,
  ChatService,
} from '@y42/collaboration/chat';
import { FontWeight, TextAlignment } from '@y42/json-schema';
import { isNotNullish, isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import debounce from 'lodash/debounce';
import flatMap from 'lodash/flatMap';
import isEqual from 'lodash/isEqual';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Observable, of, switchMap, take, tap } from 'rxjs';
import { dragCreatingToolAPI } from '../../../../tools/drag-create-tool/drag-creating-tool-api';
import { mouseCapturingToolAPI } from '../../../../tools/mouse-capturing-tool/mouse-capturing-tool-api';
import { bringToFront, sendToBack, setupBringToBackFront } from '../../../../utils/bring-to-front-back';
import { iteratorToArray } from '../../../../utils/iterator-to-array';
import { setupToolLifecycleListeners } from '../../../../utils/setup-tool-lifecycle-listeners';
import { toggleDiagramGrid } from '../../../../utils/toggle-diagram-grid';
import { toggleGuidedDraggingTool } from '../../../../utils/toggle-guided-dragging-tool';
import { getTopCenterObjectPoint } from '../../../../utils/transform-node-bounds-to-html';
import { toUniqueCollection } from '../../../../utils/unique-collection';
import { updateNodesCategories } from '../../../../utils/update-nodes-categories';
import { withTransaction } from '../../../../utils/with-transaction';
import { withTransactionDebounce } from '../../../../utils/with-transaction-debounce';
import { withoutUndoManager } from '../../../../utils/without-undo-manager';
import { contextMenuConfig, nodeType, sidebarConfig, sidebarLinkCategory, sidebarNodeTypes } from '../../config/config';
import { GojsShadow } from '../../models/gojs-shadow.model';
import { attentionNodeAPI } from '../../templates/attention-node/api/attention-node-api';
import { setupRotatingTool } from '../../templates/common/utils/setup-rotating-tool';
import { emojiNodeAPI } from '../../templates/emoji-node/api/emoji-node-api';
import { nodeGroupNodeAPI } from '../../templates/node-group-node/api/node-group-node-api';
import { exportPng } from '../../utils/export-png';
import { dragCreatingLinkToolAPI } from './../../../../tools/drag-create-link-tool/drag-creating-link-tool-api';
import { freeHandDrawingToolAPI } from './../../../../tools/free-hand-drawing-tool/free-hand-drawing-tool-api';
import { ATTENTION_NODE_PREDICATES } from './../../templates/attention-node/config/part-names';
import { BaseApi, BasePropertyApi } from './../../templates/common/api/base-api.interface';
import { LAYER_NAME_PROP, Z_ORDER_PROP } from './../../templates/common/config/common-prop-names';
import { freeDrawNodeAPI } from './../../templates/free-draw/api/free-draw-node-api';
import { groupNodeAPI } from './../../templates/group-node/api/group-node-api';
import { imageNodeAPI } from './../../templates/image-node/api/image-node-api';
import { linkAPI } from './../../templates/link/api/link-api';
import { pinNodeAPI } from './../../templates/pin-node/api/pin-node-api';
import { shapeNodeAPI } from './../../templates/shape-node/api/shape-node-api';
import { stickyNodeAPI } from './../../templates/sticky-node/api/sticky-node-api';
import { textNodeAPI } from './../../templates/text-node/api/text-node-api';
import {
  createAttentionNodeOnDiagram,
  createEmojiOnDiagram,
  createFreeHandDrawingOnDiagram,
  createImageOnDiagram,
  createLinkOnDiagram,
  createNodeGroupOnDiagram,
  createPinOnDiagram,
  createShapeOnDiagram,
  createStickyOnDiagram,
  createTextOnDiagram,
} from './../../utils/node-creation-utils';
import { setupDiagramGrid } from './../../utils/setup-diagram-grid';

const POSITION_UPDATE_DELAY = 200;

interface NodeState {
  shapeColor?: string;
  shapeBorder?: string;
  lineColor?: string;
  lineStyle?: ToolbarBorderStyle;
  textColor?: string;
  textAlignment?: TextAlignment;
  textHighlight?: string;
  fontFamily?: string;
  fontWeight?: FontWeight;
  fontSize?: number;
  shadow?: GojsShadow;
  borderStyle?: ToolbarBorderStyle;
  lock?: boolean;
  canGroup?: boolean;
  canUngroup?: boolean;
}

interface SingleChatRoomConfig {
  key: string;
  target: string;
  strategy: ChatRoomCreationStrategy;
  relatedGitEntityPath?: string;
}

@UntilDestroy()
@Component({
  selector: 'y42-gojs-sidebar',
  templateUrl: './gojs-sidebar.component.html',
  styleUrls: ['./gojs-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ChatRedirectionHelperService],
})
export class GojsSidebarComponent implements OnChanges, OnDestroy {
  constructor(
    private readonly cdRef: ChangeDetectorRef,
    private readonly chatService: ChatService,
    private readonly chatRedirectionHelperService: ChatRedirectionHelperService,
  ) {
    this.diagramSelectionChanged = this.diagramSelectionChanged.bind(this);
    this.updateMenuPosition = this.updateMenuPosition.bind(this);

    this.singleChatRoomConfig$
      .pipe(
        distinctUntilChanged((singleChatRoomConfig1, singleChatRoomConfig2) =>
          isEqual(singleChatRoomConfig1, singleChatRoomConfig2),
        ),
        switchMap((config) => {
          return this.chatRedirectionHelperService.activeChatQueryParams$.pipe(
            distinctUntilChanged((queryParams1, queryParams2) => isEqual(queryParams1, queryParams2)),
            switchMap((chatRoomQueryParams) => {
              return config
                ? this.chatService.getChatRoomsFor(config).pipe(
                    debounceTime(1000),
                    switchMap((chatRooms) => {
                      const foundChatRoom = chatRooms.find(
                        (chatRoom) => chatRoom.id === chatRoomQueryParams.chatRoomId,
                      );
                      if (foundChatRoom?.status === 'active') {
                        const foundNodes = this.diagram?.findNodesByExample({
                          key: (k: string) => k === foundChatRoom.metadata?.uiId,
                        });
                        if (foundNodes) {
                          this.diagram?.selectCollection(foundNodes); // we just select "pinNode" and let "onSelect" do the rest
                          const chatMessageId = chatRoomQueryParams[CHAT_ROOM_MESSAGE_ID_QUERY_PARAM];
                          if (chatMessageId) {
                            this.chatRedirectionHelperService.setChatRoomConfigToDisplay({
                              chatRoomId: foundChatRoom.id,
                              chatMessageId: chatRoomQueryParams[CHAT_ROOM_MESSAGE_ID_QUERY_PARAM],
                            });
                          }
                        }
                      }
                      return of(null);
                    }),
                    take(1),
                  )
                : of(null);
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  selectedObject: NodeState | null = null;
  menuConfiguration: ContextMenuOption[][] = [];
  menuVisible = false;
  menuPosition?: { x: number; y: number };

  listenerDestroyers: Array<() => void> = [];
  readonly menuConfig = contextMenuConfig;
  readonly trackByValue = trackByValue;

  private readonly showGrid$ = new BehaviorSubject<boolean>(false);
  private readonly snapToGrid$ = new BehaviorSubject<boolean>(false);

  private readonly idAndUiIdForChatRoom$ = new BehaviorSubject<{
    id: string | undefined;
    uiId: string | undefined;
  }>({ id: undefined, uiId: undefined });

  @Input()
  diagram?: go.Diagram;

  @Input()
  initialPaletteHMargin = 50;

  @Input()
  attentionNodePredicates: Array<(node: go.Node) => boolean> = [() => false];

  @Input()
  cdkDragBoundary?: HTMLElement | ElementRef<HTMLElement>;

  @Input()
  @InputObservable()
  singleChatRoomConfig: SingleChatRoomConfig | undefined = undefined;
  private readonly singleChatRoomConfig$!: Observable<SingleChatRoomConfig | undefined>;

  @Input()
  @InputObservable()
  isCanvasEditable = createAclPermissionDetail({ allowed: false });
  private readonly isCanvasEditable$!: Observable<AclPermissionDetail>;

  readonly state = connectState(this, {
    showGrid: this.showGrid$,
    snapToGrid: this.snapToGrid$,
    idAndUiIdForChatRoom: this.idAndUiIdForChatRoom$,
    isCanvasEditable: this.isCanvasEditable$,
  });

  private readonly apiMap: Record<nodeType, BaseApi<never, never>> = {
    shapeNode: shapeNodeAPI(),
    textNode: textNodeAPI(),
    imageNode: imageNodeAPI(),
    stickyNode: stickyNodeAPI(),
    pinNode: pinNodeAPI(),
    groupNode: groupNodeAPI(),
    freeDraw: freeDrawNodeAPI(),
    attentionNode: attentionNodeAPI(),
    emojiNode: emojiNodeAPI(),
    nodeGroupNode: nodeGroupNodeAPI(),
  };

  protected removeChatPinForChatRoom(chatRoom: ChatRoomUIModel) {
    const foundNodes = this.diagram?.findNodesByExample({
      key: (k: string) => k === chatRoom.metadata?.uiId,
    });
    if (foundNodes) {
      this.diagram?.selectCollection([]);
      foundNodes.each((node) => {
        const api = this.getNodeAPI(node);
        if (isNotNullish(api) && 'delete' in api) {
          (api.delete as (node: go.Node) => void)(node);
        }
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('diagram' in changes && isNotNullish(this.diagram)) {
      this.setupDiagram();
    }

    if ('attentionNodePredicates' in changes && isNotNullish(this.attentionNodePredicates)) {
      this.setupAttentionNodePredicates();
    }
  }

  activateShapeInsertionTool(svgPath: string) {
    if (this.diagram) {
      createShapeOnDiagram(this.diagram, svgPath);
    }
  }

  activateConnectionLineTool() {
    if (this.diagram) {
      createLinkOnDiagram(this.diagram);
    }
  }

  activateTextInsertionTool() {
    if (this.diagram) {
      createTextOnDiagram(this.diagram);
    }
  }

  activateDrawingTool(borderWidth: number) {
    if (this.diagram) {
      createFreeHandDrawingOnDiagram(this.diagram, borderWidth);
    }
  }

  activateImageInsertionTool(imgUrl: string) {
    if (this.diagram) {
      createImageOnDiagram(this.diagram, imgUrl);
    }
  }

  activateStickyNoteInsertionTool(color: string) {
    if (this.diagram) {
      createStickyOnDiagram(this.diagram, color);
    }
  }

  activateCommentInsertionTool() {
    if (this.diagram) {
      createPinOnDiagram(this.diagram);
    }
  }

  activateEmojiInsertionTool({ emoji }: ToolbarEmoji) {
    if (this.diagram) {
      createEmojiOnDiagram(this.diagram, emoji);
    }
  }

  activateAttentionNodeInsertionTool() {
    if (this.diagram) {
      createAttentionNodeOnDiagram(this.diagram);
    }
  }

  activateGroupNodesTool() {
    if (this.diagram) {
      createNodeGroupOnDiagram(this.diagram);
    }
  }

  onGroupSelection() {
    if (isNullish(this.diagram)) {
      console.error('No diagram!');
      return;
    }

    (groupNodeAPI().group as (diagram: go.Diagram, parts: go.Set<go.Part>) => void)(
      this.diagram,
      this.diagram.selection,
    );

    this.updateGroupingState();
    this.cdRef.markForCheck();
  }

  onUngroupSelection() {
    if (isNullish(this.diagram)) {
      console.error('No diagram!');
      return;
    }

    (groupNodeAPI().ungroup as (diagram: go.Diagram, parts: go.Set<go.Part>) => void)(
      this.diagram,
      this.diagram.selection,
    );

    this.diagram.raiseDiagramEvent('ChangedSelection', this.diagram.selection);
    this.cdRef.markForCheck();
  }

  updateNodeProperty(optionName: ContextMenuOption, value: unknown, withDebounce = false) {
    if (!this.diagram) {
      return;
    }
    const selectedNodes = this.getSelectedNodes();
    const transactionName = `update ${optionName}`;

    if (value === undefined) {
      value = null; // null is more gojs friendly than undefined
    }

    const updateProperty = () =>
      selectedNodes
        .map((node) => ({ node, api: this.getNodeAPI(node) }))
        .filter(({ api }) => optionName in api)
        .forEach(({ node, api }) => {
          (api[optionName] as BasePropertyApi).update(this.diagram!, node.data, value as never);
          this.selectedObject = {
            ...this.selectedObject,
            [optionName]: value,
          };
        });

    if (withDebounce) {
      const initialStateCallback = () =>
        selectedNodes
          .map((node) => ({ node, api: this.getNodeAPI(node) }))
          .filter(({ api }) => optionName in api)
          .reduce(
            (acc, { node, api }) => ({
              ...acc,
              [node.key as string]: (api[optionName] as BasePropertyApi).get(node.data),
            }),
            {},
          );

      const restoreInitialStateCallback = (initialValues: { [key: string]: unknown }) => {
        selectedNodes
          .map((node) => ({ node, api: this.getNodeAPI(node) }))
          .filter(({ api }) => optionName in api)
          .filter(({ node }) => (node.key as string) in initialValues)
          .forEach(({ node, api }) =>
            (api[optionName] as BasePropertyApi).update(
              this.diagram!,
              node.data,
              initialValues[node.key as string] as never,
            ),
          );
      };

      withTransactionDebounce(
        this.diagram,
        initialStateCallback,
        updateProperty,
        restoreInitialStateCallback,
        300,
        transactionName,
      );
    } else {
      withTransaction(this.diagram, updateProperty, transactionName);
    }
  }

  onBringToFront() {
    if (!this.diagram) {
      // TODO: ???
      return;
    }
    withTransaction(
      this.diagram,
      () => {
        this.getSelectedNodes()
          .sort((a, b) => a.data[Z_ORDER_PROP] - b.data[Z_ORDER_PROP])
          .forEach((item) => {
            bringToFront(item, Z_ORDER_PROP, LAYER_NAME_PROP);
          });
      },
      'bringToFront',
    );
  }

  onSendToBack() {
    if (!this.diagram) {
      // TODO: ???
      return;
    }
    withTransaction(
      this.diagram,
      () => {
        this.getSelectedNodes()
          .sort((a, b) => b.data[Z_ORDER_PROP] - a.data[Z_ORDER_PROP])
          .forEach((item) => {
            sendToBack(item, Z_ORDER_PROP, LAYER_NAME_PROP);
          });
      },
      'sendToBack',
    );
  }

  onDeleteClick() {
    const selectedNodes = this.getSelectedNodes();
    if (!this.diagram) {
      // TODO: ???
      return;
    }
    withTransaction(
      this.diagram,
      () => {
        selectedNodes.forEach((node) => {
          const api = this.getNodeAPI(node);
          if (isNotNullish(api) && 'delete' in api) {
            (api.delete as (node: go.Node) => void)(node);
          }
        });
      },
      'Delete',
    );
    if (this.diagram.currentTool) {
      this.diagram.currentTool.stopTool();
      (this.diagram.currentTool as unknown) = null; // TODO: ???
    }
    this.diagram.raiseDiagramEvent('ChangedSelection', this.diagram.selection);
  }

  onAlignNodesClick() {
    const selectedNodes = this.getSelectedNodes();
    selectedNodes.forEach((node) => {
      const api = this.getNodeAPI(node);
      if (isNotNullish(api) && 'alignNodes' in api) {
        (api.alignNodes as (group: go.Node) => void)(node);
      }
    });
  }

  onToggleShowGrid(showGrid: boolean) {
    this.showGrid$.next(showGrid);
  }

  onToggleSnapToGrid(snapToGrid: boolean) {
    this.snapToGrid$.next(snapToGrid);
  }

  onDownloadPngClick(mode: ExportMode) {
    if (this.diagram) {
      exportPng(this.diagram, mode);
    }
  }

  chatRoomCreated({ uiId, id }: { uiId: string; id: string }) {
    if (this.idAndUiIdForChatRoom$.value?.uiId && this.diagram) {
      const currentData = this.diagram.model.findNodeDataForKey(uiId);
      if (currentData) {
        this.idAndUiIdForChatRoom$.next({
          ...this.idAndUiIdForChatRoom$.value,
          id,
        });
      }
    }
  }

  ngOnDestroy() {
    this.listenerDestroyers.forEach((destroyer) => destroyer());
  }

  private getNodeAPI(node: go.Node) {
    return this.apiMap[node.category as nodeType];
  }

  private getSelectedNodes(): go.Node[] {
    // return selected sidebar nodes and group, given that group members are not in selection
    const copy = this.diagram?.selection.copy();
    return this.diagram?.selection
      .filter((n) => n instanceof go.Node)
      .filter((n: go.Part) => isNotNullish(this.getNodeAPI(n as go.Node)))
      .filter((n: go.Part) =>
        n.category === sidebarNodeTypes.groupNode ? !copy?.containsAny((n as go.Group).memberParts) : true,
      )
      .toArray() as go.Node[];
  }

  private setupDiagram() {
    const diagram = this.diagram;
    if (!diagram) {
      return;
    }
    withoutUndoManager(diagram, () => {
      this.setupNodeTemplates(diagram);
      this.setupCustomTools(diagram);
      this.setupDiagramEventListeners(diagram);
      this.adjustDiagramTools(diagram);
      updateNodesCategories(diagram, Object.keys(this.apiMap));
      setupDiagramGrid(diagram);
      mouseCapturingToolAPI.setup(diagram);
      dragCreatingToolAPI.setup(diagram);
      dragCreatingLinkToolAPI.setup(diagram);
      freeHandDrawingToolAPI.setup(diagram);

      this.showGrid$
        .pipe(
          tap((showGrid) => toggleDiagramGrid(diagram, showGrid)),
          untilDestroyed(this),
        )
        .subscribe();

      this.snapToGrid$
        .pipe(
          tap((snapToGrid) => toggleGuidedDraggingTool(diagram, snapToGrid)),
          untilDestroyed(this),
        )
        .subscribe();

      this.setupAttentionNodePredicates();
    });
  }

  private setupAttentionNodePredicates() {
    if (isNotNullish(this.diagram)) {
      (this.diagram as unknown as { [ATTENTION_NODE_PREDICATES]: unknown })[ATTENTION_NODE_PREDICATES] = // TODO: weird cast...???
        this.attentionNodePredicates;
    }
  }

  private setupCustomTools(diagram: go.Diagram) {
    this.registerListenerDestroyers([
      setupBringToBackFront(diagram, undefined, undefined, Z_ORDER_PROP, LAYER_NAME_PROP),
    ]);
  }

  private setupNodeTemplates(diagram: go.Diagram) {
    const templateProps = {
      pinNode: {
        background: sidebarConfig.colors.pinNode.backgroundColor,
        textColor: sidebarConfig.colors.pinNode.textColor,
        width: sidebarConfig.pinNode.width,
        height: sidebarConfig.pinNode.height,
        font: sidebarConfig.pinNode.font,
        onSelect: () => {
          const selectedNodes = this.getSelectedNodes();
          const selectedNode = selectedNodes?.[0];
          if (selectedNode) {
            const data = selectedNode?.data;
            this.idAndUiIdForChatRoom$.next({
              id: undefined,
              uiId: data?.key,
            });
          }
        },
        onUnselect: () => {
          this.idAndUiIdForChatRoom$.next({
            id: undefined,
            uiId: undefined,
          });
        },
      },
      groupNode: {
        background: sidebarConfig.colors.groupNode.backgroundColor,
        borderColor: sidebarConfig.colors.groupNode.borderColor,
      },
    };

    this.registerListenerDestroyers([
      ...Object.keys(this.apiMap).map((category) =>
        this.apiMap[category as nodeType].setup(
          diagram,
          category,
          templateProps[category as 'pinNode' | 'groupNode'] as never,
        ),
      ),
      linkAPI().setup(diagram, sidebarLinkCategory),
    ]);
  }

  private setupDiagramEventListeners(diagram: go.Diagram) {
    diagram.addDiagramListener('ChangedSelection', this.diagramSelectionChanged);
    diagram.addDiagramListener('SelectionMoved', this.updateMenuPosition);
    diagram.addDiagramListener('PartResized', this.updateMenuPosition);
    diagram.addDiagramListener('PartRotated', this.updateMenuPosition);
    diagram.addDiagramListener('ViewportBoundsChanged', this.updateMenuPosition);

    this.registerListenerDestroyers([
      () => {
        diagram.removeDiagramListener('ChangedSelection', this.diagramSelectionChanged);
        diagram.removeDiagramListener('SelectionMoved', this.updateMenuPosition);
        diagram.removeDiagramListener('PartResized', this.updateMenuPosition);
        diagram.removeDiagramListener('PartRotated', this.updateMenuPosition);
        diagram.removeDiagramListener('ViewportBoundsChanged', this.updateMenuPosition);
      },
    ]);
  }

  private hideAllMenus() {
    this.menuVisible = false;
  }

  private diagramSelectionChanged() {
    const selectedNodes = this.getSelectedNodes();

    this.hideAllMenus();
    this.selectedObject = null;

    if (!(selectedNodes?.length || 0)) {
      this.cdRef.markForCheck();
      return;
    }

    this.selectedObject = {
      shapeColor: this.getCommonPropertyValue(selectedNodes, 'shapeColor'),
      shapeBorder: this.getCommonPropertyValue(selectedNodes, 'shapeBorder'),
      borderStyle: this.getCommonPropertyValue(selectedNodes, 'borderStyle'),
      lock: this.getCommonPropertyValue(selectedNodes, 'lock'),
      shadow: this.getCommonPropertyValue(selectedNodes, 'shadow'),
      textColor: this.getCommonPropertyValue(selectedNodes, 'textColor'),
      textAlignment: this.getCommonPropertyValue(selectedNodes, 'textAlignment'),
      textHighlight: this.getCommonPropertyValue(selectedNodes, 'textHighlight'),
      fontFamily: this.getCommonPropertyValue(selectedNodes, 'fontFamily'),
      fontWeight: this.getCommonPropertyValue(selectedNodes, 'fontWeight'),
      fontSize: this.getCommonPropertyValue(selectedNodes, 'fontSize'),
      lineColor: this.getCommonPropertyValue(selectedNodes, 'lineColor'),
      lineStyle: this.getCommonPropertyValue(selectedNodes, 'lineStyle'),
    };

    const configurations = selectedNodes.map((node) => contextMenuConfig[this.getNodeAPI(node).name]);
    this.menuConfiguration = intersectMenuOptions(configurations);
    this.menuVisible = !!this.menuConfiguration.length;

    this.updateGroupingState();

    this.menuPosition = getTopCenterObjectPoint(selectedNodes?.[0]);
    this.cdRef.markForCheck();
  }

  private getCommonPropertyValue<T>(selectedNodes: go.Node[], apiName: string) {
    const value = toUniqueCollection(
      selectedNodes.map((node) => (this.getNodeAPI(node)[apiName] as BasePropertyApi)?.get(node.data)),
    );
    return value.length === 1 ? (value[0] as T) : undefined;
  }

  private updateGroupingState() {
    const selected = this.getSelectedNodes();
    const selectedNodes = selected.filter((x) => x.category !== sidebarNodeTypes.groupNode);
    const selectedGroups = selected.filter((x) => x.category === sidebarNodeTypes.groupNode) as go.Group[];
    this.selectedObject = {
      ...this.selectedObject,
      canGroup:
        selected.length > 1 &&
        selectedNodes.some((x) => !x.containingGroup) &&
        selectedNodes.some((x) => !flatMap(selectedGroups, (s) => iteratorToArray(s.memberParts)).includes(x)),
      canUngroup: selectedNodes.some((x) => !!x.containingGroup) || !!selectedGroups.length,
    };
  }

  private updateMenuPosition() {
    debounce(() => {
      const selectedNodes = this.getSelectedNodes();
      if (!selectedNodes?.length) {
        return;
      }

      this.menuPosition = getTopCenterObjectPoint(selectedNodes?.[0]);
      this.cdRef.markForCheck();
    }, POSITION_UPDATE_DELAY)();
  }

  private adjustDiagramTools(diagram: go.Diagram) {
    let prevVisible: boolean;
    const onActivate = () => {
      prevVisible = this.menuVisible;
      this.hideAllMenus();
      this.cdRef.markForCheck();
    };
    const onDeactivate = () => {
      setTimeout(() => {
        this.menuVisible = prevVisible;
        this.cdRef.markForCheck();
      }, POSITION_UPDATE_DELAY);
    };

    setupToolLifecycleListeners(diagram.toolManager.draggingTool, onActivate, () => {
      this.updateGroupingState();
      onDeactivate();
    });

    setupToolLifecycleListeners(diagram.toolManager.resizingTool, onActivate, onDeactivate);

    setupRotatingTool(diagram.toolManager.rotatingTool as go.RotatingTool);
  }

  private registerListenerDestroyers(destroyers: Array<() => void>) {
    this.listenerDestroyers = [...this.listenerDestroyers, ...destroyers];
  }
}
